import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { fetchDatoSpeakers } from '@src/utility/speakerUtils';
import { useEffect, useState } from 'react';

import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';

type Speakers = Awaited<ReturnType<typeof fetchDatoSpeakers>>;

const logger = LogManager.getLogger('hooks:useSpeakerHeadshot');

// Dedupe concurrent fetches for the same story (e.g. a results grid mounting
// many quote cards at once). Entry is cleared on settle so subsequent calls
// re-fetch — no TTL, no stale-data risk across navigations.
const inFlight = new Map<string, Promise<Speakers>>();

function getStorySpeakersOnce(
  storyId: string,
  datoClientStore: DatoClientStore,
): Promise<Speakers> {
  const existing = inFlight.get(storyId);
  if (existing) return existing;
  const promise = fetchDatoSpeakers(storyId, datoClientStore).finally(() => {
    inFlight.delete(storyId);
  });
  inFlight.set(storyId, promise);
  return promise;
}

type Args = {
  speakerId?: string;
  speakerName?: string;
  storyId?: string;
  datoClientStore: DatoClientStore;
};

export function useSpeakerHeadshot({
  speakerId,
  speakerName,
  storyId,
  datoClientStore,
}: Args): string | undefined {
  const [headshotUrl, setHeadshotUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!storyId) return;
    if (!speakerId && !speakerName) return;
    let cancelled = false;
    getStorySpeakersOnce(storyId, datoClientStore)
      .then(speakers => {
        if (cancelled) return;
        const match = speakerId
          ? speakers.find(s => s.id === speakerId)
          : speakers.find(
              s => s.name?.toLowerCase() === speakerName?.toLowerCase(),
            );
        setHeadshotUrl(match?.headshots?.[0]?.url);
      })
      .catch(err => {
        if (cancelled) return;
        logger.warn('Failed to fetch speaker headshots', extractErrorInfo(err));
      });
    return () => {
      cancelled = true;
    };
  }, [speakerId, speakerName, storyId, datoClientStore]);

  return headshotUrl;
}
